// WANTED poster maker.
//
// The point is a picture the visitor can keep: their own face on a Marine
// wanted poster, downloaded as a real image file. So the preview IS the
// download — everything is drawn once into a 1200x1800 canvas and the screen
// just shows that canvas scaled down. What you see is the file you get.
//
// The photo never leaves the device: it is read with FileReader and drawn in
// the browser. Nothing is uploaded, stored or sent anywhere, and the page says
// so, because people will not put their face in a box that does not.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import characterProfiles from '../data/characterProfiles';
import animeConfig from '../config/animeConfig';
import QuizPageText from '../components/QuizPageText/QuizPageText';
import { wantedPosterText } from '../components/QuizPageText/quizPageText.data';
import { trackPosterPicture, trackPosterFinish } from '../services/analyticsService';
import './WantedPoster.css';

// Every face in the archive, not the sixteen from the avatar picker: the row
// scrolls, so there is no reason to hold most of the cast back.
const shortName = (full) => {
  const name = full.trim();
  const m = name.match(/^(.+?)\s+the\s+/i);
  return (m ? m[1] : name).split(/\s+/).pop();
};

const FACES = characterProfiles
  .filter((c) => c.image)
  .map((c) => ({ slug: c.slug, name: c.name, short: shortName(c.name), image: c.image }));

const W = 1200;
const H = 1800;

// The seas, in the order the story visits them.
const SEAS = ['East Blue', 'Paradise', 'Grand Line', 'New World'];

// Epithets read like the ones in the story: a thing, not a compliment.
const EPITHETS = [
  'Straw Hat', 'Pirate Hunter', 'Cat Burglar', 'Black Leg', 'Devil Child',
  'Sniper King', 'Cyborg', 'Soul King', 'Fire Fist', 'Surgeon of Death',
  'Red-Haired', 'Dark King', 'Big Eater', 'Iron Fist', 'Sea Devil',
];

// Bounty tiers, so a reroll lands on a number that means something in the
// story rather than a random pile of digits.
const TIERS = [
  { key: 'smallfry', label: 'Small Fry', min: 500_000, max: 9_000_000 },
  { key: 'rookie', label: 'Rookie', min: 10_000_000, max: 90_000_000 },
  { key: 'supernova', label: 'Supernova', min: 100_000_000, max: 900_000_000 },
  { key: 'emperor', label: 'Emperor', min: 1_000_000_000, max: 4_800_000_000 },
];

const rollBounty = (tierKey) => {
  const tier = TIERS.find((t) => t.key === tierKey) || TIERS[2];
  const step = tier.min >= 1_000_000_000 ? 100_000_000
    : tier.min >= 100_000_000 ? 10_000_000
    : tier.min >= 10_000_000 ? 1_000_000
    : 100_000;
  const steps = Math.floor((tier.max - tier.min) / step) + 1;
  return tier.min + Math.floor(Math.random() * steps) * step;
};

const WantedPoster = () => {
  const canvasRef = useRef(null);
  const fileRef = useRef(null);
  const imageRef = useRef(null);          // the photo being drawn
  const dragRef = useRef(null);           // pointer drag state

  const [name, setName] = useState('');
  const [epithet, setEpithet] = useState('');
  const [sea, setSea] = useState('Grand Line');
  const [tier, setTier] = useState('supernova');
  const [bounty, setBounty] = useState(() => rollBounty('supernova'));
  const [customBounty, setCustomBounty] = useState('');
  const [charIdx, setCharIdx] = useState(0);
  const [usingPhoto, setUsingPhoto] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);

  // Upper-casing is a Latin idea; leave every other script as the person typed it.
  const typedName = name.trim();
  const displayName = typedName
    ? (/[a-z]/i.test(typedName) && !/[\u0590-\u08FF]/.test(typedName) ? typedName.toUpperCase() : typedName)
    : 'YOUR NAME';
  const amount = customBounty.trim()
    ? Math.max(0, Number(customBounty.replace(/[^0-9]/g, '')) || 0)
    : bounty;

  // --- load whichever picture is selected -------------------------------
  const loadImage = useCallback((src) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      imageRef.current = img;
      setOffset({ x: 0, y: 0 });
      setZoom(1);
      setReady(true);
    };
    img.onerror = () => { imageRef.current = null; setReady(true); };
    img.src = src;
  }, []);

  useEffect(() => {
    if (!usingPhoto) loadImage(FACES[charIdx].image);
  }, [charIdx, usingPhoto, loadImage]);

  const onPickFile = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      trackPosterPicture('photo');
      setUsingPhoto(true);
      loadImage(reader.result);   // a data URL: stays in this browser
    };
    reader.readAsDataURL(file);
  };

  // --- the poster, drawn once, shown and downloaded from the same canvas --
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const ink = '#2a1c0c';

    // aged paper
    const paper = ctx.createLinearGradient(0, 0, W, H);
    paper.addColorStop(0, '#efe0bb');
    paper.addColorStop(0.5, '#e7d4a8');
    paper.addColorStop(1, '#dcc695');
    ctx.fillStyle = paper;
    ctx.fillRect(0, 0, W, H);

    // stains, so it does not look like a clean web page
    for (let i = 0; i < 140; i++) {
      const r = 20 + Math.random() * 90;
      ctx.beginPath();
      ctx.fillStyle = `rgba(120, 85, 35, ${0.012 + Math.random() * 0.03})`;
      ctx.arc(Math.random() * W, Math.random() * H, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // border
    ctx.strokeStyle = ink;
    ctx.lineWidth = 10;
    ctx.strokeRect(34, 34, W - 68, H - 68);
    ctx.lineWidth = 3;
    ctx.strokeRect(54, 54, W - 108, H - 108);

    // Letter spacing is drawn character by character, which is fine for Latin
    // and wrong for Arabic: splitting the word breaks the joins and reverses
    // the order. Any script that joins or runs right to left is drawn in one
    // piece and keeps its shaping.
    const joinsOrRtl = (text) => /[\u0590-\u08FF\u200F\uFB1D-\uFDFF\uFE70-\uFEFF]/.test(text);

    const centre = (text, y, font, color = ink, spacing = 0) => {
      ctx.font = font;
      ctx.fillStyle = color;
      ctx.textAlign = 'center';
      if (!spacing || joinsOrRtl(text)) { ctx.fillText(text, W / 2, y); return; }
      const chars = [...text];
      const widths = chars.map((c) => ctx.measureText(c).width + spacing);
      let x = W / 2 - (widths.reduce((a, b) => a + b, 0) - spacing) / 2;
      chars.forEach((c, i) => { ctx.textAlign = 'left'; ctx.fillText(c, x, y); x += widths[i]; });
      ctx.textAlign = 'center';
    };

    centre('WANTED', 196, '900 150px Georgia, "Times New Roman", serif', ink, 10);

    // photo frame
    const fx = 130, fy = 260, fw = W - 260, fh = 760;
    ctx.save();
    ctx.beginPath();
    ctx.rect(fx, fy, fw, fh);
    ctx.clip();
    ctx.fillStyle = '#c9b68c';
    ctx.fillRect(fx, fy, fw, fh);
    const img = imageRef.current;
    if (img && img.width) {
      const base = Math.max(fw / img.width, fh / img.height);
      const scale = base * zoom;
      const dw = img.width * scale;
      const dh = img.height * scale;
      ctx.drawImage(img, fx + (fw - dw) / 2 + offset.x, fy + (fh - dh) / 2 + offset.y, dw, dh);
    } else {
      ctx.fillStyle = 'rgba(42,28,12,0.45)';
      ctx.font = '600 44px Georgia, serif';
      ctx.textAlign = 'center';
      ctx.fillText('Add your photo', W / 2, fy + fh / 2);
    }
    ctx.restore();
    ctx.strokeStyle = ink;
    ctx.lineWidth = 8;
    ctx.strokeRect(fx, fy, fw, fh);

    centre('DEAD OR ALIVE', 1110, '900 86px Georgia, serif', ink, 6);

    if (epithet.trim()) {
      centre(`"${epithet.trim()}"`, 1192, 'italic 600 52px Georgia, serif', 'rgba(42,28,12,0.78)');
    }

    // the name, shrunk to fit rather than spilling out of the poster
    let nameSize = 120;
    ctx.font = `900 ${nameSize}px Georgia, serif`;
    while (ctx.measureText(displayName).width > W - 220 && nameSize > 46) {
      nameSize -= 4;
      ctx.font = `900 ${nameSize}px Georgia, serif`;
    }
    centre(displayName, epithet.trim() ? 1320 : 1300, `900 ${nameSize}px Georgia, serif`, ink, 4);

    // bounty line
    const money = amount.toLocaleString('en-US');
    let moneySize = 104;
    ctx.font = `900 ${moneySize}px Georgia, serif`;
    while (ctx.measureText(`${money} -`).width > W - 330 && moneySize > 40) {
      moneySize -= 4;
      ctx.font = `900 ${moneySize}px Georgia, serif`;
    }
    ctx.textAlign = 'center';
    ctx.fillStyle = ink;
    const berryX = W / 2 - ctx.measureText(`${money} -`).width / 2 - 70;
    ctx.font = `900 ${moneySize}px Georgia, serif`;
    ctx.fillText(`${money} -`, W / 2 + 36, 1452);
    ctx.font = `900 ${moneySize + 6}px Georgia, serif`;
    ctx.fillText('฿', berryX + 24, 1452);

    // marine line
    ctx.beginPath();
    ctx.moveTo(150, 1520);
    ctx.lineTo(W - 150, 1520);
    ctx.strokeStyle = 'rgba(42,28,12,0.55)';
    ctx.lineWidth = 4;
    ctx.stroke();

    centre('MARINE', 1640, '900 96px Georgia, serif', ink, 14);
    centre(sea.toUpperCase(), 1706, '600 42px Georgia, serif', 'rgba(42,28,12,0.7)', 6);

    // corner marks, like a pinned paper
    ctx.fillStyle = 'rgba(42,28,12,0.25)';
    [[90, 90], [W - 90, 90], [90, H - 90], [W - 90, H - 90]].forEach(([x, y]) => {
      ctx.beginPath(); ctx.arc(x, y, 14, 0, Math.PI * 2); ctx.fill();
    });
  }, [displayName, epithet, amount, sea, zoom, offset]);

  useEffect(() => { draw(); }, [draw, ready]);

  // --- dragging the photo inside the frame ------------------------------
  const pointerDown = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    dragRef.current = { x: e.clientX, y: e.clientY, scale: W / rect.width, start: { ...offset } };
    canvasRef.current.setPointerCapture(e.pointerId);
  };
  const pointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    setOffset({
      x: d.start.x + (e.clientX - d.x) * d.scale,
      y: d.start.y + (e.clientY - d.y) * d.scale,
    });
  };
  const pointerUp = (e) => {
    dragRef.current = null;
    if (canvasRef.current.hasPointerCapture(e.pointerId)) canvasRef.current.releasePointerCapture(e.pointerId);
  };

  const posterFacts = () => ({
    usedPhoto: usingPhoto,
    tier: customBounty.trim() ? 'custom' : tier,
    sea,
    bounty: amount,
    named: !!name.trim(),
    epithet: epithet.trim().slice(0, 40),
  });

  const download = async () => {
    setBusy(true);
    draw();
    const canvas = canvasRef.current;
    await new Promise((resolve) => {
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(name.trim() || 'wanted').replace(/[^a-z0-9_-]/gi, '_').toLowerCase()}-wanted-poster.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
        trackPosterFinish('download', posterFacts());
        resolve();
      }, 'image/png');
    });
    setBusy(false);
  };

  // Sharing sends the poster itself, not a link to it — a picture is what gets
  // posted. Phones that cannot share a file fall back to the text and link.
  const share = async () => {
    const text = `My bounty is \u0e3f${amount.toLocaleString('en-US')}. Make your own at https://www.onepiecetrivia.com/wanted`;
    try {
      draw();
      const blob = await new Promise((resolve) => canvasRef.current.toBlob(resolve, 'image/png'));
      const file = new File([blob], 'one-piece-wanted-poster.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], text });
      } else if (navigator.share) {
        await navigator.share({ title: 'My WANTED poster', text });
      } else {
        await navigator.clipboard.writeText(text);
      }
      trackPosterFinish('share', posterFacts());
    } catch (e) { /* the person closed the share sheet */ }
  };

  return (
    <>
      <Helmet>
        <title>One Piece WANTED Poster Maker — Put Your Face on a Bounty</title>
        <meta name="description" content="Make a One Piece WANTED poster with your own photo, your pirate name and your bounty, then download it as an image. Free, no signup, and your photo never leaves your device." />
        <link rel="canonical" href="https://www.onepiecetrivia.com/wanted" />
        <meta property="og:title" content="One Piece WANTED Poster Maker" />
        <meta property="og:description" content="Your face, your pirate name, your bounty — downloaded as a real poster." />
        <meta property="og:url" content="https://www.onepiecetrivia.com/wanted" />
        <meta property="og:image" content="https://www.onepiecetrivia.com/og-wanted.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="One Piece WANTED Poster Maker" />
        <meta name="twitter:description" content="Your face, your pirate name, your bounty — downloaded as a real poster." />
        <meta name="twitter:image" content="https://www.onepiecetrivia.com/og-wanted.png" />
      </Helmet>

      <div
        className="wp-wrapper"
        style={{
          backgroundImage: `linear-gradient(to bottom, rgba(0,0,0,0.72), rgba(0,0,0,0.9)), url(${animeConfig.onePiece.backgroundImage})`,
        }}
      >
      <div className="wp-page">
        <h1 className="wp-title">Make Your WANTED Poster</h1>
        <p className="wp-sub">Your photo, your pirate name, your bounty — then download it.</p>

        <div className="wp-layout">
          <div className="wp-stage">
            <canvas
              ref={canvasRef}
              width={W}
              height={H}
              className="wp-canvas"
              onPointerDown={pointerDown}
              onPointerMove={pointerMove}
              onPointerUp={pointerUp}
              onPointerCancel={pointerUp}
            />
            <p className="wp-hint">Drag the picture to move it, and use the slider to zoom.</p>
            <div className="wp-actions">
              <button type="button" className="wp-btn wp-btn-primary" onClick={download} disabled={busy}>
                {busy ? 'Making it…' : 'Download'}
              </button>
              <button type="button" className="wp-btn" onClick={share}>Share</button>
            </div>
          </div>

          <div className="wp-controls">
            <label className="wp-field">
              <span>Your picture</span>
              <div className="wp-photo-row">
                <button type="button" className="wp-btn wp-btn-photo" onClick={() => fileRef.current.click()}>
                  Upload a photo
                </button>
                {usingPhoto && (
                  <button type="button" className="wp-btn wp-btn-ghost" onClick={() => { setUsingPhoto(false); }}>
                    Use a character
                  </button>
                )}
              </div>
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPickFile} />
              <small className="wp-note">Your photo stays on your device. It is never uploaded.</small>
            </label>

            {!usingPhoto && (
              <div className="wp-faces-wrap">
                <div className="wp-faces-head">
                  <span>Or pick a character</span>
                  <span className="wp-swipe">swipe for all {FACES.length} &rarr;</span>
                </div>
                <div className="wp-faces">
                  {FACES.map((c, i) => (
                    <button
                      key={c.slug}
                      type="button"
                      className={`wp-face ${i === charIdx ? 'is-on' : ''}`}
                      onClick={() => { trackPosterPicture('character', c.name); setCharIdx(i); }}
                      aria-label={c.name}
                      title={c.name}
                    >
                      <img src={c.image} alt="" loading="lazy" />
                      <span>{c.short}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <label className="wp-field">
              <span>Zoom</span>
              <input type="range" min="1" max="3" step="0.01" value={zoom}
                onChange={(e) => setZoom(Number(e.target.value))} className="wp-range" />
            </label>

            <label className="wp-field">
              <span>Pirate name</span>
              <input className="wp-input" maxLength={22} value={name}
                onChange={(e) => setName(e.target.value)} placeholder="Straw Hat Mak" />
            </label>

            <label className="wp-field">
              <span>Epithet</span>
              <input className="wp-input" maxLength={24} value={epithet}
                onChange={(e) => setEpithet(e.target.value)} placeholder='e.g. "Pirate Hunter"' />
              <div className="wp-chips">
                {EPITHETS.slice(0, 6).map((e) => (
                  <button type="button" key={e} className="wp-chip" onClick={() => setEpithet(e)}>{e}</button>
                ))}
                <button type="button" className="wp-chip wp-chip-action"
                  onClick={() => setEpithet(EPITHETS[Math.floor(Math.random() * EPITHETS.length)])}>
                  Surprise me
                </button>
              </div>
            </label>

            <label className="wp-field">
              <span>Sea</span>
              <div className="wp-chips">
                {SEAS.map((s) => (
                  <button type="button" key={s}
                    className={`wp-chip ${s === sea ? 'is-on' : ''}`}
                    onClick={() => setSea(s)}>{s}</button>
                ))}
              </div>
            </label>

            <label className="wp-field">
              <span>Bounty</span>
              <div className="wp-chips">
                {TIERS.map((t) => (
                  <button type="button" key={t.key}
                    className={`wp-chip ${t.key === tier && !customBounty ? 'is-on' : ''}`}
                    onClick={() => { setTier(t.key); setCustomBounty(''); setBounty(rollBounty(t.key)); }}>
                    {t.label}
                  </button>
                ))}
                <button type="button" className="wp-chip wp-chip-action"
                  onClick={() => { setCustomBounty(''); setBounty(rollBounty(tier)); }}>
                  Reroll
                </button>
              </div>
              <input className="wp-input" inputMode="numeric" value={customBounty}
                onChange={(e) => setCustomBounty(e.target.value)} placeholder="or type your own number" />
            </label>

          </div>
        </div>
      </div>
      </div>

      <QuizPageText {...wantedPosterText} />
    </>
  );
};

export default WantedPoster;
